import React from "react";
import { useCartManagement } from "@/hooks/useCartManagement";
import { CartUpdateButton } from "@/components/cartUpdateButton/CartUpdateButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Plus, Minus, Trash2, User, UserCheck } from "lucide-react";

export const CartExample: React.FC = () => {
  const {
    cartItems,
    cartTotal,
    cartItemCount,
    isLoading,
    isAuthenticated,
    isGuest,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    clearCart,
    refetchCart,
    isAddingToCart,
    isUpdatingCart,
    isRemovingFromCart,
    isClearingCart,
  } = useCartManagement();

  const handleAddSampleItem = () => {
    const sampleProduct = {
      id: `sample-${Date.now()}`,
      name: "Sample Product",
      price: 29.99,
      image: "https://via.placeholder.com/150",
      brand: "Sample Brand"
    };
    
    addToCart(sampleProduct, 1);
  };

  const handleUpdateQuantity = (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
    } else {
      updateCartItemQuantity(itemId, newQuantity);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <ShoppingCart className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold">Cart Management</h1>
            <p className="text-gray-600">
              {isAuthenticated ? (
                <span className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-green-600" />
                  Authenticated User - Backend Cart
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <User className="h-4 w-4 text-orange-600" />
                  Guest User - Local Cart
                </span>
              )}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <Badge variant="secondary">
            {cartItemCount} items
          </Badge>
          <Badge variant="default">
            ${cartTotal.toFixed(2)}
          </Badge>
          {isAuthenticated && (
            <CartUpdateButton
              onUpdate={refetchCart}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>

      {/* Add Sample Item Button */}
      <div className="mb-6">
        <Button onClick={handleAddSampleItem} disabled={isAddingToCart}>
          <Plus className="h-4 w-4 mr-2" />
          {isAddingToCart ? 'Adding...' : 'Add Sample Item'}
        </Button>
      </div>

      {/* Cart Items */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading cart...</p>
        </div>
      ) : cartItems.length === 0 ? (
        <div className="text-center py-12">
          <ShoppingCart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
          <p className="text-gray-600 mb-4">
            {isAuthenticated 
              ? "Items you add to your cart will be saved to your account."
              : "Items you add to your cart will be saved locally."
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {cartItems.map((item: any) => (
            <div key={item.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-4">
                <img 
                  src={item.image || item.product?.image || "https://via.placeholder.com/60"} 
                  alt={item.name || item.product?.name}
                  className="w-15 h-15 object-cover rounded"
                />
                <div>
                  <h3 className="font-medium">
                    {item.name || item.product?.name || "Unknown Product"}
                  </h3>
                  <p className="text-sm text-gray-600">
                    ${(item.price || item.product?.price || 0).toFixed(2)}
                  </p>
                  {item.variant && (
                    <p className="text-xs text-gray-500">
                      Variant: {JSON.stringify(item.variant)}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUpdateQuantity(item.id, (item.quantity || 1) - 1)}
                    disabled={isUpdatingCart || isRemovingFromCart}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="w-8 text-center">{item.quantity || 1}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUpdateQuantity(item.id, (item.quantity || 1) + 1)}
                    disabled={isUpdatingCart}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
                
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => removeFromCart(item.id)}
                  disabled={isRemovingFromCart}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          
          {/* Cart Actions */}
          <div className="flex justify-between items-center pt-4 border-t">
            <div>
              <p className="text-lg font-medium">
                Total: ${cartTotal.toFixed(2)}
              </p>
              <p className="text-sm text-gray-600">
                {cartItemCount} {cartItemCount === 1 ? 'item' : 'items'}
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={clearCart}
                disabled={isClearingCart}
              >
                {isClearingCart ? 'Clearing...' : 'Clear Cart'}
              </Button>
              <Button disabled={cartItems.length === 0}>
                Proceed to Checkout
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 